import { useLinkageAmount, useLinkageBusinessAPI } from '../LinkageCreationContext'
import { useContext, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import Lottie from 'lottie-react'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import GlobalContext from '../../../../../../../context/global-context'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'
import CustomButton from '../../../../../ui/CustomButton'
import ResizableModal from '../../../../../ui/ResizableModal'
import { Title } from '../../../../../ui'

const InputField = ({ label, placeholder, type = 'text', value = '', onChange, disabled = false }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm font-sans ${
        disabled
          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
          : 'focus:border-indigo-500 focus:ring-indigo-500'
      }`}
    />
  </div>
)

const formatRut = value => {
  const cleaned = String(value || '').replace(/[^0-9kK]/g, '').toUpperCase().slice(0, 9)
  if (cleaned.length < 2) return cleaned
  const body = cleaned.slice(0, -1)
  const dv = cleaned.slice(-1)
  return `${new Intl.NumberFormat('es-CL').format(Number(body))}-${dv}`
}

const formatAmount = value => {
  const numericValue = String(value || '').replace(/\D/g, '')
  return numericValue ? new Intl.NumberFormat('es-CL').format(numericValue) : ''
}

const AnticipoVinculacion = ({
  open,
  setOpen,
  type,
  setSwitchDate,
  idLibro,
  folio,
  action,
  title,
  slug,
  counterpartyLabel,
  amountLabel,
  classification,
  showRut = true
}) => {
  const { currentBusiness } = useContext(GlobalContext)
  const [fecha, setFecha] = useState('')
  const [glosa, setGlosa] = useState('')
  const [monto, setMonto, bankAmountLocked] = useLinkageAmount('')
  const [rut, setRut] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [data, setData] = useState({})
  const [razonesSociales, setRazonesSociales] = useState([])
  const [loading, setLoading] = useState(false)
  const businessAPI = useLinkageBusinessAPI()
  const organizationAPI = new OrganizationAPI()

  const handleClose = () => setOpen(false)

  const loadData = async () => {
    if (!idLibro) return
    const response = await organizationAPI.getDataLibroContable(
      currentBusiness?.id,
      idLibro,
      type.trim()
    )
    setData(response.data || {})
  }

  useEffect(() => {
    if (!open) return

    const loadModal = async () => {
      setLoading(true)
      try {
        if (idLibro) await loadData()
        if (showRut) {
          const response = await organizationAPI.getRazonSocial(currentBusiness?.id)
          setRazonesSociales(Array.isArray(response.data) ? response.data : [])
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }

    loadModal()
  }, [open, idLibro])

  useEffect(() => {
    setFecha(data.fecha ? new Date(data.fecha).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
    setGlosa(data.glosa || '')
    setMonto(data.montoTotal?.toString() || '')
    setRazonSocial(data.razonSocial || '')
    setRut(data.rut || '')
  }, [data, open])

  const createLinkage = async () => {
    const payload = {
      fecha,
      transaccionId: null,
      razonSocial: razonSocial || null,
      ...(showRut && { rut, rutId: null }),
      nombreComprobante: null,
      nombreComprobanteId: null,
      glosa: glosa || null,
      respaldoId: null,
      monto: Number(monto)
    }
    await businessAPI.postPagoArriendo(currentBusiness.id, payload, slug)
  }

  const updateLinkage = async () => {
    const selectedCounterparty = showRut
      ? razonesSociales.find(item => item.rut === rut)
      : null
    const requests = [
      organizationAPI.updateVinculacion(currentBusiness?.id, idLibro, {
        fecha,
        glosa,
        monto: Number(monto),
        razonSocial,
        ...(showRut && { rut }),
        nombreComprobante: data.nombreCodigoDocumento
      })
    ]

    if (selectedCounterparty?.code) {
      requests.push(
        organizationAPI.patchRazonSocial(
          currentBusiness.id,
          selectedCounterparty.code.split('-')[0],
          { razonSocial }
        )
      )
    }

    await Promise.all(requests)
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      if (action === 'modificar') await updateLinkage()
      else await createLinkage()
      setSwitchDate(true)
      handleClose()
    } catch (error) {
      console.error(`Error al guardar ${title}:`, error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      footer={
        <>
          <CustomButton title='CANCELAR' color='primary' className='rounded-full' onClick={handleClose} />
          <CustomButton
            title={action === 'replicar' ? 'REPLICAR VINCULACIÓN' : 'GRABAR VINCULACIÓN'}
            color='accent2'
            className='rounded-full'
            onClick={handleSubmit}
          />
        </>
      }
    >
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <Lottie animationData={LoaderMain} loop autoplay style={{ width: 100, height: 100 }} />
        </Box>
      ) : (
        <div className='mt-4'>
          <div className='flex justify-between items-center mb-6'>
            <Title className='text-3xl font-sans'>{title}</Title>
          </div>
          <div className='flex w-full items-start h-full p-6 bg-white rounded-lg shadow-md'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
              <InputField label='Folio' value={folio} disabled />
              <InputField label='Fecha' type='date' value={fecha} onChange={event => setFecha(event.target.value)} />
              <InputField label={counterpartyLabel} value={razonSocial} onChange={event => setRazonSocial(event.target.value)} />
              {showRut && (
                <InputField label='RUT' placeholder='11.111.111-1' value={formatRut(rut)} onChange={event => setRut(formatRut(event.target.value))} />
              )}
              <InputField disabled={bankAmountLocked} label={amountLabel} placeholder='$' value={formatAmount(monto)} onChange={event => setMonto(event.target.value.replace(/[^0-9]/g, ''))} />
              <InputField label='Clasificación' placeholder={classification} disabled />
              <div>
                <label className='block text-sm font-sans font-medium text-gray-700'>Detalle</label>
                <textarea
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
                  placeholder='Describe en detalle la naturaleza, origen o lo que mejor describa la vinculación que vamos a crear.'
                  value={glosa}
                  onChange={event => setGlosa(event.target.value)}
                  maxLength={50}
                  required
                />
                <p className='text-xs text-gray-500 mt-1 font-sans'>{glosa.length}/50 caracteres</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </ResizableModal>
  )
}

export default AnticipoVinculacion
